/**
 * Unit inspect panel — the click-pinned "who is that unit?" surface.
 *
 * Left-click a unit (enemy/ally player or a pet) pins this panel bottom-left; it
 * shows the unit's name, relation, level, HP, and gear, and updates live until
 * dismissed (Esc / click empty ground / the target dies or leaves fog-of-war).
 * It complements `hover-tooltip.ts` — hover = transient glance, click = pinned read.
 *
 * Design: `docs/superpowers/specs/2026-06-30-unit-inspect-panel-design.md`
 * (Option C — Minimal: header + HP + equipment; no stat block).
 *
 * Client-only: every field is read from the locally-synced Colyseus state, no
 * network round-trip — a visible unit's full `Cube`/`Creature` schema (incl. the
 * `equipment` map and pet `posture`) is already on the client (fog-of-war culls
 * what you can't see, so "clickable" ⇒ "fully synced"). main.ts owns the pick +
 * per-frame refresh (mirroring the hover path) and feeds `InspectContent` here;
 * this module owns the DOM. Pinning/target-tracking live in main.ts so the panel
 * stays a dumb, swappable renderer.
 *
 * The content builders below are pure (no DOM) and mirror the hover builders so
 * names/icons can't diverge from the rest of the HUD.
 */

import { creatureDisplayName } from "../display-names";
import { itemIconUrl, hasRealItemIcon } from "../items/item-icons";
import { ITEMS, type EquipmentSlotKey } from "@tribez/shared";

// ── content model ────────────────────────────────────────────────────────────

export type InspectRelation = "ally" | "enemy" | "neutral";

export type InspectSlot = {
  /** Short slot tag under the icon. */
  label: string;
  itemId: string;
  /** Item display name ("" when the slot is empty). */
  name: string;
  /** Icon URL (real PNG or the item-icons SVG fallback); null when empty. */
  iconUrl: string | null;
  empty: boolean;
};

export type InspectContent = {
  /** Stable id for swap detection — re-selecting the SAME unit is idempotent. */
  key: string;
  kind: "cube" | "pet";
  name: string;
  relation: InspectRelation;
  level: number;
  hp: { cur: number; max: number };
  /** Main-hand real-art icon for players (shown in the portrait); null → plain disc. */
  portraitIcon: string | null;
  /** Players only — the 3 equip slots in fixed order. */
  slots?: InspectSlot[];
  /** Pets only — owner label + current stance. */
  pet?: { owner: string; posture: string };
};

// ── content builders (pure — no DOM) ──────────────────────────────────────────

/** Equip slots in display order, with their short tags. Mirrors EQUIPMENT_SLOT_KEYS. */
const SLOT_META: { key: EquipmentSlotKey; label: string }[] = [
  { key: "main-hand", label: "Main" },
  { key: "offhand", label: "Off" },
  { key: "armor", label: "Armor" },
];

function slotView(itemId: string, label: string): InspectSlot {
  const empty = itemId === "";
  return {
    label,
    itemId,
    name: empty ? "" : ITEMS[itemId]?.displayName ?? itemId,
    iconUrl: empty ? null : itemIconUrl(itemId),
    empty,
  };
}

/**
 * Player-cube inspect content. `equip` is the raw itemId per slot ("" = empty),
 * extracted from the synced `Cube.equipment` map by the caller.
 */
export function inspectCube(
  sessionId: string,
  name: string,
  level: number,
  hp: number,
  hpMax: number,
  relation: InspectRelation,
  equip: Partial<Record<EquipmentSlotKey, string>>,
): InspectContent {
  const mainId = equip["main-hand"] ?? "";
  return {
    key: `cube:${sessionId}`,
    kind: "cube",
    name: name || "Kobold",
    relation,
    level,
    hp: { cur: hp, max: hpMax },
    portraitIcon: mainId && hasRealItemIcon(mainId) ? itemIconUrl(mainId) : null,
    slots: SLOT_META.map((m) => slotView(equip[m.key] ?? "", m.label)),
  };
}

/**
 * Pet inspect content. Pets are creatures with an owner — no gear slots, so the
 * body shows owner + stance instead. `ownerName` should already be resolved to
 * "You" (self) or the owner's display name by the caller.
 */
export function inspectPet(
  id: string,
  creatureType: string,
  level: number,
  hp: number,
  hpMax: number,
  relation: InspectRelation,
  ownerName: string,
  posture: string,
): InspectContent {
  return {
    key: `pet:${id}`,
    kind: "pet",
    name: creatureDisplayName(creatureType),
    relation,
    level,
    hp: { cur: hp, max: hpMax },
    portraitIcon: null,
    pet: { owner: ownerName || "—", posture: posture || "Follow" },
  };
}

// ── DOM panel ─────────────────────────────────────────────────────────────────

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : "&quot;");
}

export type InspectPanel = {
  /** `content=null` hides the panel; non-null renders + shows it. */
  update(content: InspectContent | null): void;
  dispose(): void;
};

export function createInspectPanel(): InspectPanel {
  const el = document.createElement("div");
  el.id = "inspect-panel";
  el.classList.add("hidden");
  document.body.appendChild(el);

  let visible = false;

  // Icon-only slot tiles — the icon carries the meaning; an empty slot simply reads
  // as a blank tile (no "empty"/name/slot-type text). `title` gives a native hover
  // label for the equipped item without adding visible chrome.
  function slotHtml(s: InspectSlot): string {
    if (s.empty) {
      return `<div class="ip-slot ip-slot--empty"><div class="ip-slot__ic"></div></div>`;
    }
    return `<div class="ip-slot" title="${esc(s.label)}: ${esc(s.name)}">` +
      `<div class="ip-slot__ic" style="background-image:url('${s.iconUrl}')"></div></div>`;
  }

  function render(c: InspectContent): void {
    const relCls = c.relation !== "neutral" ? ` ip-nm--${c.relation}` : "";
    const badge =
      c.relation === "ally" ? `<span class="ip-badge ip-badge--ally">Ally</span>`
      : c.relation === "enemy" ? `<span class="ip-badge ip-badge--enemy">Enemy</span>`
      : "";

    const portrait = c.portraitIcon
      ? `<div class="ip-por" style="background-image:url('${c.portraitIcon}')"></div>`
      : `<div class="ip-por ip-por--plain ip-por--${c.relation}"></div>`;

    const meta =
      c.kind === "pet"
        ? `${badge}<span>${esc(c.pet!.owner === "You" ? "Your Pet" : c.pet!.owner)} · Lvl ${c.level}</span>`
        : `${badge}<span>Level ${c.level}</span>`;

    const low = c.hp.max > 0 && c.hp.cur / c.hp.max < 0.25 ? " ip-hp--low" : "";
    const pct = c.hp.max > 0 ? Math.max(0, Math.min(100, (c.hp.cur / c.hp.max) * 100)) : 0;
    const bar =
      `<div class="ip-bar${low}"><i style="width:${pct}%"></i>` +
      `<b>${Math.ceil(c.hp.cur)} / ${c.hp.max}</b></div>`;

    let body: string;
    if (c.kind === "pet") {
      body = `<div class="ip-petline"><span>Owner</span><b>${esc(c.pet!.owner)}</b>` +
        `<span>Stance</span><b>${esc(c.pet!.posture)}</b></div>`;
    } else {
      body = `<div class="ip-equip">${c.slots!.map(slotHtml).join("")}</div>`;
    }

    el.innerHTML =
      `<div class="ip-hd">${portrait}<div class="ip-id">` +
      `<div class="ip-nm${relCls}">${esc(c.name)}</div>` +
      `<div class="ip-mt">${meta}</div></div></div>` +
      `<div class="ip-bd">${bar}${body}</div>`;
  }

  return {
    update(content) {
      if (!content) {
        if (visible) { el.classList.add("hidden"); visible = false; }
        return;
      }
      // Re-render every call so live HP / gear / stance track state.
      render(content);
      if (!visible) { el.classList.remove("hidden"); visible = true; }
    },
    dispose() {
      el.remove();
    },
  };
}
